import api from '../../services/api';

export default {
    namespaced: true,
    state: {
        all: [],
        adminAll: [],
        current: null,
        currentlyEditedInstance: null,
        paging: {
            total: 0,
            page: 1,
            pageSize: 15,
        },
        errors: null
    },
    getters: {
        phpVersionsTable(state) {
            const instance = state.currentlyEditedInstance;
            // Always show these PHP versions
            const allVersions = [
                { version: '7.4', displayable: 'PHP 7.4', apiVersion: 'php74' },
                { version: '8.1', displayable: 'PHP 8.1', apiVersion: 'php81' },
                { version: '8.2', displayable: 'PHP 8.2', apiVersion: 'php82' },
                { version: '8.3', displayable: 'PHP 8.3', apiVersion: 'php83' },
                { version: '8.4', displayable: 'PHP 8.4', apiVersion: 'php84' },
            ];
            // Helper to parse backend version string (e.g., 'php84' -> '8.4')
            function parseDisplayVersion(backendVersion) {
                const match = backendVersion.match(/^php(\d)(\d)$/);
                if (match) return `${match[1]}.${match[2]}`;
                return backendVersion;
            }
            // Map installed versions for quick lookup
            const installed = (instance && instance.php_versions)
                ? instance.php_versions.map(ver => {
                    const attrs = ver.attributes || ver;
                    // Try to get display version from backend, or parse from version string
                    let displayVersion = attrs.displayableVersion || attrs.displayable_version;
                    if (!displayVersion && attrs.version) displayVersion = parseDisplayVersion(attrs.version);
                    // Find the correct allVersions entry
                    const found = allVersions.find(v => v.version === displayVersion || v.apiVersion === attrs.version);
                    return {
                        version: found ? found.version : displayVersion,
                        apiVersion: found ? found.apiVersion : attrs.version,
                        installed: (attrs.status === 'installed' || attrs.installed === true),
                        usedAsDefault: attrs.usedAsDefault || attrs.used_as_default || false,
                        usedOnCli: attrs.usedOnCli || attrs.used_on_cli || false,
                    };
                })
                : [];
            // For each desired version, use installed info if present, else mark as installable
            return allVersions.map(v => {
                const found = installed.find(i => i.apiVersion === v.apiVersion);
                if (found) return found;
                return {
                    version: v.version,
                    apiVersion: v.apiVersion,
                    installed: false,
                    usedAsDefault: false,
                    usedOnCli: false,
                };
            });
        },
    },
    actions: {
        retrieveInstances ({ commit }) {
            return new Promise((resolve) => {
                api.instances.list(instances => {
                    commit('setAll', instances);
                    resolve();
                });
            });
        },
        retrieveAdminInstances ({ commit }, { page, pageSize }) {
            return new Promise((resolve) => {
                api.instances.adminList(page, pageSize, result => {
                    const meta = result.meta || result;

                    const [total, pageSize, currentPage] = [meta.last_page, meta.per_page, meta.current_page];
                    commit('setPaging', { total, pageSize, currentPage });
                    commit('setAdminAll', result.data);
                    commit('setCurrentlyEditedInstance', null);
                    resolve();
                });
            });
        },
        retrieveInstanceDetails ({ commit }, instanceId) {
            return new Promise((resolve) => {
                api.instances.get(instanceId, result => {
                    commit('setCurrentlyEditedInstance', result);
                    resolve();
                });
            });
        },
        createInstance ({ dispatch, commit }, instance) {
            return new Promise((resolve) => {
                api.instances.create(instance, result => {
                    resolve(result);
                    dispatch('notifications/queueNotification', 'Successfully created instance', { root: true });
                }, (error) => {
                    commit('setErrors', error.response.data.errors);
                });
            });
        },
        updateInstance ({ commit, dispatch }, instance) {
            return new Promise((resolve) => {
                api.instances.update(instance, result => {
                    commit('setCurrentlyEditedInstance', result);
                    dispatch('notifications/queueNotification', 'Successfully updated instance', { root: true });
                    resolve();
                });
            });
        },
        installPhpVersion({ commit, dispatch, state }, version) {
            const instanceId = state.currentlyEditedInstance?.id;
            if (!instanceId || !version) return Promise.reject('Missing instance ID or version');
            return new Promise((resolve, reject) => {
                api.instances.installPhpVersion(
                    instanceId,
                    version,
                    (data) => {
                        commit('setCurrentlyEditedInstance', data);
                        resolve(data);
                    },
                    (error) => {
                        dispatch('notifications/queueNotification', `Failed to install PHP ${version}.`, { root: true });
                        reject(error);
                    }
                );
            });
        },
        fetchEventDetails(context, { serverId, eventId }) {
            return new Promise((resolve, reject) => {
                api.instances.getEvent(
                    serverId,
                    eventId,
                    (data) => resolve(data),
                    (error) => reject(error)
                );
            });
        },
    },
    mutations: {
        setAll (state, instances) {
            state.all = instances;
        },
        setAdminAll (state, instances) {
            state.adminAll = instances;
        },
        setCurrentInstance (state, instance) {
            state.current = instance;
        },
        setCurrentlyEditedInstance (state, instance) {
            state.currentlyEditedInstance = instance;
        },
        setPaging (state, { total, pageSize, currentPage }) {
            state.paging = { total, pageSize, currentPage };
        },
        setErrors (state, errors) {
            state.errors = errors;
        }
    }
};
